import {useNavigate} from "react-router-dom";
import React, {useMemo, useState} from "react";
import { faSquareCaretRight } from "@fortawesome/free-solid-svg-icons";
import {getAuth, signOut} from "firebase/auth";
import {logEvent} from "firebase/analytics";
import {analytics} from "../../../../firebaseConfig";
import SectionDivider from "./SectionDivider";
import MenuButton from "./MenuButton";
import LogoutDialog from "./components/DialogLogout/LogoutDialog";
import BrandLogo from "./BrandLogo";
import LogoutSection from "./LogoutSection";
import {platformMenuItems, lineOaMenuItems} from "./menuItems";

interface SidebarProps {
  userName: string;
  userRole: string;
  userPermissions: object;
  userOrganizationNames: string[];
  mode: string;
  onClickLogo?: () => void;
}

const PLATFORM_TITLE_ITEM = {name: '貼心到宅平台', isDivider: true, path: '', icon: faSquareCaretRight}
const LINE_OA_TITLE_ITEM = {name: 'LINE官方帳號', isDivider: true, path: '', icon: faSquareCaretRight}

export const Sidebar = ({
  userName,
  userRole,
  userPermissions,
  userOrganizationNames,
  mode = 'desktop',
  onClickLogo = () => null
}: SidebarProps) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [dialogContent, setDialogContent] = useState<'confirmLogout' | ''>('');
  const navigate = useNavigate();
  const auth = getAuth();

  const onClickLogoutButton = () => {
    setDialogContent('confirmLogout')
  };

  const handleDontLogout = () => {
    setDialogContent('')
  }

  const handleLogout = async () => {
    try {
      await signOut(auth);
      logEvent(analytics, 'logoutSuccess', { userName })
      navigate("/login"); // Redirect to login after logout
    } catch (error) {
      logEvent(analytics, 'logoutFail', { userName })
      console.error("Logout failed:", error);
    }
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed)
    logEvent(analytics, "brand_logo_click", { name: `${isCollapsed ? 'collapsed' : 'not_collapsed'}`})
  }

  const availablePlatformMenuItems = useMemo(() => {
    const filteredMenuItems = platformMenuItems.filter(menuItem => {
      return userRole && menuItem.role.includes(userRole)
    })
    if (filteredMenuItems.length > 0) {
      return [PLATFORM_TITLE_ITEM, ...filteredMenuItems]
    } else {
      return []
    }
  }, [userRole])

  const availableLineOaMenuItems = useMemo(() => {
    const filteredMenuItems = lineOaMenuItems.filter(menuItem => {
      return userPermissions && menuItem.permissions.some(key => userPermissions[key] === true);
    })
    if (filteredMenuItems.length > 0) {
      return [LINE_OA_TITLE_ITEM, ...filteredMenuItems]
    } else {
      return []
    }
  }, [userPermissions])

  return (
    <React.Fragment>
      <div
        style={{ backgroundColor: "rgba(55, 125, 157, 1)" }}
        className={`
          text-white transition-all duration-300 flex flex-col h-screen justify-between
          ${isCollapsed ? "w-20" : "w-64"}
        `}
      >
        {/* Brand Logo */}
        <div className="px-4 pt-4">
          <BrandLogo
            mode={mode}
            toggleCollapse={() => setIsCollapsed(!isCollapsed)}
            onClickLogo={onClickLogo}
            isCollapsed={isCollapsed}
          />
        </div>

        {/* Menu Buttons */}
        <ul className={"flex flex-col flex-1 overflow-y-scroll p-4"}>
          {isCollapsed ? (
            <MenuButton
              key="toggleCollapse"
              isCollapsed
              icon={faSquareCaretRight}
              onClick={toggleCollapse}
              name="Menu"
            />
          ) : null}
          {[...availablePlatformMenuItems, ...availableLineOaMenuItems].map((item) => (
            <div key={item.name}>
              {item.isDivider
                ? <>
                  {!isCollapsed
                    ? <SectionDivider label={item.name} />
                    : null}
                </>
                : (
                  <MenuButton
                    path={item.path}
                    name={item.name}
                    icon={item.icon}
                    isCollapsed={isCollapsed}
                  />
                )
              }
            </div>
          ))}
        </ul>

        {/* Logout Section */}
        <LogoutSection
          isCollapsed={isCollapsed}
          userName={userName}
          userRole={userRole}
          userOrganizationNames={userOrganizationNames}
          onClickLogout={onClickLogoutButton}
        />
      </div>

      <LogoutDialog
        isShow={dialogContent === 'confirmLogout'}
        onClickDontLogout={handleDontLogout}
        onClickLogout={handleLogout}
      />
    </React.Fragment>
  );
};
